import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    VIEW, EDIT,
    WATERFALL, V_MODEL, SPIRAL_MODEL, AGILE_MODEL,
} from "const"
import { Lifecycle, LifecycleCreate } from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    LifecycleValidation,
    SaveLifecycle,
    GetMaxLifecycles,
    closeModal,
    setLifecycleEdit,
    setLifecycleReloadList,
    setLifecycleReloadView,
    setLifecycleReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    SelectDropdown,
    SelectDropdownItem,
    Delimiter,
    UserDropdown,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditLifecycleProps {
    lifecycle: Lifecycle
}

let isMounted: boolean

const Form = ({
    lifecycle,
}: EditLifecycleProps) => {
    const baseRoute = useSelector((state: StoreType) => state.lifecycle.baseRoute)

    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(lifecycle.name)
    const [description, setDescription] = useInput(lifecycle.description)

    const [type, setType] = useState(lifecycle.type)
    const [responsible, setResponsible] = useState(lifecycle.responsible)
    const [isDefault, setIsDefault] = useState(lifecycle.default)

    const [validation, setValidation] = useState<LifecycleValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = lifecycle.id
            ? `${baseRoute}/${VIEW}/${lifecycle.id}`
            : baseRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newLifecycleData: LifecycleCreate = {
            originalLifecycleId: lifecycle.originalLifecycleId || null,
            accountId: lifecycle.accountId || userData.accountId,
            projectId: lifecycle.projectId || project.id,
            name,
            description,
            type,
            ownerAccount: lifecycle.ownerAccount || userData.accountId,
            creator: lifecycle.id ? lifecycle.creator : userData.userId,
            responsible,
            order: lifecycle.id ? lifecycle.order : 0,
            default: isDefault,
        }

        const { maxLifecycles } = GetMaxLifecycles(packageSettings, userData)
        SaveLifecycle(
            lifecycle.id,
            newLifecycleData,
            maxLifecycles,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newLifecycleId) replaceHistoryState(`${baseRoute}/${EDIT}/${res.newLifecycleId}`)
            }
            if (res.validation) return

            dispatch(setLifecycleEdit({
                id: res.newLifecycleId || lifecycle.id,
                ...newLifecycleData,
            }))

            dispatch(setLifecycleReloadList(false))
            dispatch(setLifecycleReloadView(false))
            setTimeout(() => {
                dispatch(setLifecycleReloadList(true))
                dispatch(setLifecycleReloadView(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setLifecycleReloadEdit(false))
                setTimeout(() => {
                    dispatch(setLifecycleReloadEdit(true))
                }, 0)
            }
        })
    }

    const accountUsers = (accountUserList && accountUserList[userData.accountId]) || []
    const projectUsers = (projectUserList && projectUserList[project.id]) || []

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.type}</Label>
                        <SelectDropdown title={lang[type] || ""}>
                            <SelectDropdownItem onClick={() => setType(WATERFALL)}>{lang.waterfall}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setType(V_MODEL)}>{lang.v_model}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setType(SPIRAL_MODEL)}>{lang.spiral_model}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setType(AGILE_MODEL)}>{lang.agile_model}</SelectDropdownItem>
                        </SelectDropdown>
                    </FormGroup>
                    {
                        !project.id && (
                            <FormGroup>
                                <Label>{lang.default}</Label>
                                <SelectDropdown title={isDefault ? lang.yes : lang.no}>
                                    <SelectDropdownItem onClick={() => setIsDefault(true)}>{lang.yes}</SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => setIsDefault(false)}>{lang.no}</SelectDropdownItem>
                                </SelectDropdown>
                            </FormGroup>
                        )
                    }
                </Col>

                {
                    userData.accountId !== 1 && (
                        <Col xs={12}>
                            <Delimiter />
                            <FormGroup>
                                <Label>{lang.responsible}</Label>
                                <UserDropdown
                                    selectedId={responsible}
                                    listIds={!project.id ? accountUsers : projectUsers}
                                    title={lang.changeResponsible}
                                    onChange={(userId) => setResponsible(userId)}
                                    showDelete
                                />
                            </FormGroup>
                            <Delimiter />
                        </Col>
                    )
                }

                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
